/**
 * Turns a structured threat model into a control→threat *coverage* model, deterministically.
 *
 * Every node and edge here is computed from the payload alone — threats from each finding's
 * `vulnClass`, coverage from each control's own `mitigates` list. Nothing reads prose to decide
 * connections. The one piece of authored logic is {@link THREAT_TAXONOMY}: a fixed keyword table that
 * normalizes both `vulnClass` strings and `mitigates` tags into a shared vocabulary so the two can be
 * joined ("cross-site scripting" and "XSS" become the same threat). It is intentionally small and
 * auditable — when the API grows a structured `mitigatedBy` reference, this join is deleted and the
 * references are used directly.
 */

export type ThreatRelevance = 'present' | 'likely' | 'unlikely' | 'not_applicable'

export type SecurityControl = {
  name: string
  kind: string
  /** Free-text tags the generator emits for what this control defends against. */
  mitigates: string[]
  scope?: string
}

export type VulnerabilityClassImpact = {
  vulnClass: string
  relevance: ThreatRelevance
  reachability?: string
  blastRadius?: string
}

export type ThreatModelCoverageInput = {
  repoName?: string
  internetFacing?: string
  controls: SecurityControl[]
  vulnerabilities: VulnerabilityClassImpact[]
}

export type CoverageControl = {
  id: string
  name: string
  kind: string
  mitigates: string[]
  scope?: string
  /** Ids of the threats this control's `mitigates` tags map onto. */
  coveredThreatIds: string[]
}

export type CoverageThreat = {
  id: string
  /** Canonical threat name, or the raw `vulnClass` when no taxonomy entry matched. */
  name: string
  relevance: ThreatRelevance
  /** The original `vulnClass` strings that normalized to this threat. */
  sources: string[]
  reachability?: string
  blastRadius?: string
  mitigatingControlIds: string[]
  /** A present/likely threat that no control claims to mitigate. */
  isGap: boolean
}

export type CoverageModel = {
  controls: CoverageControl[]
  threats: CoverageThreat[]
}

/**
 * Fixed threat vocabulary. Order matters: the first entries win, so `CSRF` is tested before the looser
 * `request forgery` that also matches SSRF, and specific classes precede the `injection` catch-all.
 */
export const THREAT_TAXONOMY: readonly { name: string; pattern: RegExp }[] = [
  { name: 'CSRF', pattern: /csrf|cross[\s-]*site request forgery/i },
  { name: 'XSS', pattern: /xss|cross[\s-]*site scripting|html injection|markup injection|content injection/i },
  { name: 'SQL injection', pattern: /sql|jpql/i },
  { name: 'SSRF', pattern: /ssrf|outbound (http|request)|server[\s-]*side request/i },
  { name: 'Auth / AuthZ / IDOR', pattern: /idor|authoriz|access control|broken auth|missing auth|tenant isolation/i },
  { name: 'AuthN / credentials', pattern: /authentication|credential/i },
  { name: 'Path / traversal', pattern: /path traversal|zip slip|traversal|arbitrary file|file[\s-]*(read|write)/i },
  { name: 'Upload / archive', pattern: /upload|archive/i },
  { name: 'Deserialization', pattern: /deserial|unsafe object/i },
  { name: 'DoS / exhaustion', pattern: /denial of service|resource exhaustion|\bdos\b|amplification|backpressure/i },
  { name: 'Info disclosure', pattern: /information (disclosure|exposure)|sensitive[\s-]*(info|data)|logging|\bleak/i },
  { name: 'XXE / XML', pattern: /xxe|xml external/i },
  { name: 'Open redirect', pattern: /open redirect|redirect/i },
  { name: 'Cmd / RCE', pattern: /command injection|shell|remote code|\brce\b/i },
  { name: 'Injection (generic)', pattern: /injection/i },
] as const

/** Returns the canonical threat names a piece of text mentions, in taxonomy order. */
export const bucketizeThreat = (text: string): string[] => {
  const matched: string[] = []
  for (const { name, pattern } of THREAT_TAXONOMY) {
    if (pattern.test(text)) {
      matched.push(name)
    }
  }
  // `Injection (generic)` is a catch-all for text that only says "injection"; when a specific class
  // (SQL, command, …) already matched, the generic entry is a duplicate and is dropped.
  if (matched.length > 1) {
    return matched.filter(name => name !== 'Injection (generic)')
  }
  return matched
}

const RELEVANCE_ORDER: Record<ThreatRelevance, number> = {
  present: 0,
  likely: 1,
  unlikely: 2,
  not_applicable: 3,
}

/** A threat is worth flagging as a gap only when it is actually expected to matter. */
const isGapRelevance = (relevance: ThreatRelevance): boolean => relevance === 'present' || relevance === 'likely'

type ThreatDraft = {
  name: string
  relevance: ThreatRelevance
  sources: string[]
  reachability?: string
  blastRadius?: string
}

/**
 * Builds the coverage model. Threats that share a canonical name are merged, keeping the strongest
 * (most relevant) instance's relevance and prose. A `vulnClass` that matches no taxonomy entry is kept
 * verbatim as its own threat rather than dropped, so nothing silently disappears.
 */
export const buildCoverageModel = ({ controls, vulnerabilities }: ThreatModelCoverageInput): CoverageModel => {
  const drafts = new Map<string, ThreatDraft>()
  for (const vulnerability of vulnerabilities) {
    const names = bucketizeThreat(vulnerability.vulnClass)
    const threatNames = names.length > 0 ? names : [vulnerability.vulnClass]
    for (const name of threatNames) {
      const existing = drafts.get(name)
      if (!existing) {
        drafts.set(name, {
          name,
          relevance: vulnerability.relevance,
          sources: [vulnerability.vulnClass],
          reachability: vulnerability.reachability,
          blastRadius: vulnerability.blastRadius,
        })
        continue
      }
      existing.sources.push(vulnerability.vulnClass)
      if (RELEVANCE_ORDER[vulnerability.relevance] < RELEVANCE_ORDER[existing.relevance]) {
        existing.relevance = vulnerability.relevance
        existing.reachability = vulnerability.reachability
        existing.blastRadius = vulnerability.blastRadius
      }
    }
  }

  const orderedDrafts = [...drafts.values()].sort(
    (first, second) =>
      RELEVANCE_ORDER[first.relevance] - RELEVANCE_ORDER[second.relevance] || first.name.localeCompare(second.name)
  )
  const threatIdByName = new Map(orderedDrafts.map((draft, index) => [draft.name, `threat-${index}`]))

  const coverageControls: CoverageControl[] = controls.map((control, index) => {
    const coveredThreatIds: string[] = []
    for (const tag of control.mitigates) {
      for (const name of bucketizeThreat(tag)) {
        const threatId = threatIdByName.get(name)
        if (threatId && !coveredThreatIds.includes(threatId)) {
          coveredThreatIds.push(threatId)
        }
      }
    }
    return {
      id: `control-${index}`,
      name: control.name,
      kind: control.kind,
      mitigates: control.mitigates,
      scope: control.scope,
      coveredThreatIds,
    }
  })

  const threats: CoverageThreat[] = orderedDrafts.map(draft => {
    const id = threatIdByName.get(draft.name) ?? draft.name
    const mitigatingControlIds = coverageControls
      .filter(control => control.coveredThreatIds.includes(id))
      .map(control => control.id)
    return {
      id,
      name: draft.name,
      relevance: draft.relevance,
      sources: draft.sources,
      reachability: draft.reachability,
      blastRadius: draft.blastRadius,
      mitigatingControlIds,
      isGap: mitigatingControlIds.length === 0 && isGapRelevance(draft.relevance),
    }
  })

  return { controls: coverageControls, threats }
}
